import { Plus, X } from 'lucide-react';
import React, { useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import api from '../../service/api';
import type {
  CardAction,
  EffectDurationType,
  EffectType,
  ShopItem,
} from '../../service/api/types';

interface CardActionsEditorProps {
  show: boolean;
  onHide: () => void;
  onSave: (actions: CardAction[]) => void;
  initialActions?: CardAction[];
  title?: string;
}

interface StickerAction {
  type: 'sticker';
  amount: number;
}

interface ItemAction {
  type: 'item';
  item: string;
  amount: number;
}

interface EffectAction {
  type: 'effect';
  effectType: EffectType;
  targetItem: string;
  value: number;
  durationType: EffectDurationType;
  durationMinutes: number;
}

type ActionToEdit = StickerAction | ItemAction | EffectAction;

export const CardActionsEditor: React.FC<CardActionsEditorProps> = ({
  show,
  onHide,
  onSave,
  initialActions = [],
  title,
}) => {
  const { t } = useTranslation();
  const modalTitle = title || t('cards.editCardActions');

  const [actions, setActions] = useState<ActionToEdit[]>(() =>
    initialActions.map(action => {
      if (action.type === 'item') {
        return {
          ...action,
          item:
            typeof action.item === 'string'
              ? action.item
              : action.item?._id || '',
        };
      }
      if (action.type === 'effect') {
        return {
          ...action,
          targetItem:
            action.targetItem && typeof action.targetItem !== 'string'
              ? action.targetItem._id
              : action.targetItem || '',
          durationMinutes: action.durationMinutes ?? 60,
        } as EffectAction;
      }
      return action as StickerAction;
    }),
  );
  const [shopItems, setShopItems] = useState<ShopItem[]>([]);
  const [isLoadingShopItems, setIsLoadingShopItems] = useState(false);

  // Re-initialize actions from initialActions every time the modal opens
  React.useEffect(() => {
    if (show) {
      setActions(
        initialActions.map(action => {
          if (action.type === 'item') {
            return {
              ...action,
              item:
                typeof action.item === 'string'
                  ? action.item
                  : action.item?._id || '',
            };
          }
          if (action.type === 'effect') {
            return {
              ...action,
              targetItem:
                action.targetItem && typeof action.targetItem !== 'string'
                  ? action.targetItem._id
                  : action.targetItem || '',
              durationMinutes: action.durationMinutes ?? 60,
            } as EffectAction;
          }
          return action as StickerAction;
        }),
      );
    }
  }, [show, initialActions]);

  React.useEffect(() => {
    if (show) {
      setIsLoadingShopItems(true);
      api
        .shopItemsGet()
        .then(setShopItems)
        .catch(console.error)
        .finally(() => setIsLoadingShopItems(false));
    }
  }, [show]);

  const addStickerAction = () => {
    setActions(prev => [
      ...prev,
      { type: 'sticker', amount: 0 } as StickerAction,
    ]);
  };

  const addItemAction = () => {
    setActions(prev => [
      ...prev,
      { type: 'item', item: '', amount: 1 } as ItemAction,
    ]);
  };

  const addEffectAction = () => {
    setActions(prev => [
      ...prev,
      {
        type: 'effect',
        effectType: 'adjust_all_prices_percent',
        targetItem: '',
        value: -10,
        durationType: 'end_of_day',
        durationMinutes: 60,
      } as EffectAction,
    ]);
  };

  const removeAction = (index: number) => {
    setActions(prev => prev.filter((_, i) => i !== index));
  };

  const updateAction = (index: number, updates: Partial<ActionToEdit>) => {
    setActions(prev =>
      prev.map((action, i) => {
        if (i === index) {
          return { ...action, ...updates } as ActionToEdit;
        }
        return action;
      }),
    );
  };

  const handleSave = () => {
    // Convert back to CardAction format for API
    const formattedActions: CardAction[] = actions.map(action => {
      if (action.type === 'item') {
        const shopItem = shopItems.find(item => item._id === action.item);
        return {
          ...action,
          item: shopItem,
        };
      }
      if (action.type === 'effect') {
        const shopItem = action.targetItem
          ? shopItems.find(item => item._id === action.targetItem)
          : undefined;
        return {
          type: 'effect' as const,
          effectType: action.effectType,
          targetItem: shopItem,
          value: action.value,
          durationType: action.durationType,
          durationMinutes: action.durationMinutes,
        };
      }
      return action;
    });

    onSave(formattedActions);
    onHide();
  };

  const isValid = () => {
    return (
      actions.length > 0 &&
      actions.every(action => {
        if (action.type === 'sticker') {
          return action.amount !== 0;
        }
        if (action.type === 'item') {
          return action.item && action.amount !== 0;
        }
        if (action.type === 'effect') {
          const needsTarget =
            action.effectType === 'set_price' ||
            action.effectType === 'adjust_item_price_percent';
          if (needsTarget && !action.targetItem) return false;
          if (
            action.durationType === 'duration_minutes' &&
            (!action.durationMinutes || action.durationMinutes < 1)
          )
            return false;
          return true;
        }
        return false;
      })
    );
  };

  const needsTargetItem = (effectType: EffectType) =>
    effectType === 'set_price' || effectType === 'adjust_item_price_percent';

  const getEffectTypeLabel = (effectType: EffectType) => {
    switch (effectType) {
      case 'set_price':
        return t('effects.types.setPrice');
      case 'adjust_all_prices_percent':
        return t('effects.types.adjustAllPricesPercent');
      case 'adjust_item_price_percent':
        return t('effects.types.adjustItemPricePercent');
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{modalTitle}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={addStickerAction}
            className="me-2"
          >
            <Plus size={16} className="me-1" />
            {t('cards.actions.addStickerAction')}
          </Button>
          <Button
            variant="outline-success"
            size="sm"
            onClick={addItemAction}
            disabled={isLoadingShopItems}
            className="me-2"
          >
            <Plus size={16} className="me-1" />
            {t('cards.actions.addItemAction')}
          </Button>
          <Button
            variant="outline-warning"
            size="sm"
            onClick={addEffectAction}
            disabled={isLoadingShopItems}
          >
            <Plus size={16} className="me-1" />
            {t('cards.actions.addEffectAction')}
          </Button>
        </div>

        {actions.length === 0 && (
          <div className="text-muted text-center py-4">
            {t('cards.actions.addAtLeastOne')}
          </div>
        )}

        {actions.map((action, index) => (
          <div key={index} className="border rounded p-3 mb-3">
            <Row className="align-items-center">
              <Col>
                {action.type === 'sticker' ? (
                  <Form.Group>
                    <Form.Label>{t('cards.actions.stickerAmount')}</Form.Label>
                    <Form.Control
                      type="number"
                      value={action.amount || ''}
                      onChange={e =>
                        updateAction(index, {
                          amount: parseInt(e.target.value) || 0,
                        })
                      }
                      placeholder={t('cards.actions.enterAmountPlaceholder')}
                    />
                    <Form.Text className="text-muted">
                      {t('cards.actions.stickerHelp')}
                    </Form.Text>
                  </Form.Group>
                ) : action.type === 'item' ? (
                  <Row>
                    <Col md={8}>
                      <Form.Group>
                        <Form.Label>{t('cards.actions.shopItem')}</Form.Label>
                        <Form.Select
                          value={action.item}
                          onChange={e =>
                            updateAction(index, { item: e.target.value })
                          }
                        >
                          <option value="">
                            {t('cards.actions.selectItem')}
                          </option>
                          {shopItems.map(item => (
                            <option key={item._id} value={item._id}>
                              {item.title} (${item.price})
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>{t('cards.actions.amount')}</Form.Label>
                        <Form.Control
                          type="number"
                          value={action.amount || ''}
                          onChange={e =>
                            updateAction(index, {
                              amount: parseInt(e.target.value) || 0,
                            })
                          }
                        />
                        <Form.Text className="text-muted">
                          {t('cards.actions.itemHelp')}
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                ) : (
                  /* Effect action */
                  <div>
                    <Row className="mb-2">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>{t('effects.effectType')}</Form.Label>
                          <Form.Select
                            value={action.effectType}
                            onChange={e => {
                              const newType = e.target.value as EffectType;
                              updateAction(index, {
                                effectType: newType,
                                targetItem: needsTargetItem(newType)
                                  ? action.targetItem
                                  : '',
                              });
                            }}
                          >
                            {(
                              [
                                'set_price',
                                'adjust_all_prices_percent',
                                'adjust_item_price_percent',
                              ] as EffectType[]
                            ).map(et => (
                              <option key={et} value={et}>
                                {getEffectTypeLabel(et)}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>{t('effects.value')}</Form.Label>
                          <Form.Control
                            type="number"
                            value={action.value || ''}
                            onChange={e =>
                              updateAction(index, {
                                value: parseFloat(e.target.value) || 0,
                              })
                            }
                          />
                          <Form.Text className="text-muted">
                            {action.effectType === 'set_price'
                              ? t('effects.absolutePrice')
                              : t('effects.percentageHelp')}
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>
                    {needsTargetItem(action.effectType) && (
                      <Row className="mb-2">
                        <Col>
                          <Form.Group>
                            <Form.Label>{t('effects.targetItem')}</Form.Label>
                            <Form.Select
                              value={action.targetItem}
                              onChange={e =>
                                updateAction(index, {
                                  targetItem: e.target.value,
                                })
                              }
                            >
                              <option value="">
                                {t('cards.actions.selectItem')}
                              </option>
                              {shopItems.map(item => (
                                <option key={item._id} value={item._id}>
                                  {item.title} (${item.price})
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      </Row>
                    )}
                    <Row>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>{t('effects.durationType')}</Form.Label>
                          <Form.Select
                            value={action.durationType}
                            onChange={e =>
                              updateAction(index, {
                                durationType: e.target
                                  .value as EffectDurationType,
                              })
                            }
                          >
                            <option value="end_of_day">
                              {t('effects.endOfDay')}
                            </option>
                            <option value="duration_minutes">
                              {t('effects.durationMinutes')}
                            </option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      {action.durationType === 'duration_minutes' && (
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>
                              {t('effects.durationMinutes')}
                            </Form.Label>
                            <Form.Control
                              type="number"
                              min={1}
                              value={action.durationMinutes || ''}
                              onChange={e =>
                                updateAction(index, {
                                  durationMinutes:
                                    parseInt(e.target.value) || 0,
                                })
                              }
                            />
                          </Form.Group>
                        </Col>
                      )}
                    </Row>
                  </div>
                )}
              </Col>
              <Col xs="auto">
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => removeAction(index)}
                >
                  <X size={16} />
                </Button>
              </Col>
            </Row>
          </div>
        ))}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {t('common.cancel')}
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={!isValid()}>
          {t('cards.actions.saveActions')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
